import "./App.css"
import Plan from "./Pages/Plan"
import Register from "./Pages/Register"
import Resources from "./Pages/Resources"
import { useAtom } from "jotai"
import { redirectAtom, userAtom } from "./state"
import { useEffect, useState } from "react"
import {
  BrowserRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
  useMatch,
  useOutletContext,
} from "react-router-dom"
import api, { apiClient } from "./services/api"
import Login from "./Pages/Login"
import ForgotPassword from "./Pages/ForgotPassword"
import ResetPassword from "./Pages/ResetPassword"
import MagicLinkVerify from "./Pages/MagicLinkVerify"
import PlanInvitationAccept from "./Pages/PlanInvitationAccept"
import Users from "./Pages/Users"
import AdminPlans from "./Pages/Admin/AdminPlans"
import AdminPlanDetail from "./Pages/Admin/AdminPlanDetail"
import AdminDashboard from "./Pages/Admin/AdminDashboard"
import AdminOrganizations from "./Pages/Admin/AdminOrganizations"
import AdminOrganizationDetail from "./Pages/Admin/AdminOrganizationDetail"
import Home from "./Pages/Home"
import SuperAdminRoute from "./Components/Layout/SuperAdminRoute"
import { Dev, DevRoutes } from "./Pages/dev/Dev"
import { OnboardingFlow } from "./Flows/OnboardingFlow"
import OnboardingTourFlow from "./Flows/OnboardingTourFlow"
import { PlanNewFlow } from "./Flows/PlanNewFlow"
import { SetupChecklist } from "./Flows/Setup/SetupChecklist"
import ChooseGuide from "./Flows/Setup/ChooseGuide"
import GuideInviteSent from "./Flows/Setup/GuideInviteSent"
import InviteStudent from "./Flows/Setup/InviteStudent"
import SitWithStudent from "./Flows/Setup/SitWithStudent"
import InviteMentors from "./Flows/Setup/InviteMentors"
import SendPlan from "./Flows/Setup/SendPlan"
import PlanContent from "./Components/PlanContent"
import NotFound from "./Pages/NotFound"
import ErrorBoundary from "./Components/ErrorBoundary"
import NoPlans from "./Pages/NoPlans"
import StudentInviteSent from "./Flows/Setup/StudentInviteSent"
import MentorsInviteSent from "./Flows/Setup/MentorsInviteSent"
import PlanComplete from "./Flows/Setup/PlanComplete"
import { sentryService } from "./services/sentry"
import { identifyClarityUser } from "./services/clarity"
import PlanDownload from "./Pages/PlanDownload"
import MentorActivities from "./Flows/Setup/MentorActivies"
import FAQ from "./Pages/FAQ"
import AdminFAQ from "./Pages/Admin/AdminFAQ"
import DebugModal from "./Components/DebugModal"
import { useDebugShortcut } from "./hooks/useDebugShortcut"
import CookieConsentBanner from "./Components/CookieConsentBanner"

// Wrapper component to forward outlet context to nested routes
function OutletContextForwarder() {
  const context = useOutletContext()
  return <Outlet context={context} />
}

function App() {
  const [user, setUser] = useAtom(userAtom)

  const [ready, setReady] = useState(false)

  const [debugOpen, setDebugOpen] = useDebugShortcut()

  useEffect(() => {
    // Fetch CSRF cookie first, then get user
    const baseUrl = import.meta.env.VITE_API_BASE.replace(/\/api\/?$/, "")
    apiClient
      .get(baseUrl + "/sanctum/csrf-cookie")
      .then(() => api.getUser())
      .then(response => {
        setUser(response.data)
        sentryService.setUserContext(response.data)
        identifyClarityUser(response.data)
        setReady(true)
      })
      .catch(() => {
        setReady(true)
        sentryService.setUserContext(null)
      })

    const bc = new BroadcastChannel("auth")
    bc.onmessage = event => {
      if (event.data.type === "logged_in" || event.data.type === "logged_out") {
        if (!window.location.pathname.startsWith("/magic-link/")) {
          window.location.reload()
        }
      }
    }
    return () => bc.close()
  }, [])
  if (!user && !ready) {
    return null
  }

  return (
    <>
      <BrowserRouter>
        <ErrorBoundary>
          <Routes>
            {/* Magic link verification needs to handle both logged-in and logged-out users */}
            <Route path="/magic-link/:token" element={<MagicLinkVerify />} />

            <Route path="/faq" element={<FAQ />} />

            <Route element={<UnauthRoute />}>
              <Route path="/login" element={<Login />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password/:token" element={<ResetPassword />} />
              <Route path="/register/:org?" element={<Register />} />
              <Route path="/register" element={<Outlet />}>
                <Route path="" element={<Register />} />
                <Route path="new" element={<OnboardingFlow />} />
              </Route>
            </Route>
            <Route element={<AuthRoute />}>
              <Route path="/" element={<Home />} />
              <Route path="/resources" element={<Resources />} />
              <Route path="/users/:organization_slug?" element={<Users />} />
              <Route path="/plans/:organization_slug?" element={<AdminPlans />} />
              <Route path="/resources" element={<Resources />} />
              <Route element={<AdminRoute />}>
                <Route path="/admin" element={<Outlet />}>
                  <Route index element={<AdminDefaultRoute />} />
                  <Route path="dashboard" element={<AdminDashboard />} />
                  <Route path="summary" element={<AdminDashboard />} />

                  {/* Super Admin Only Routes */}
                  <Route element={<SuperAdminRoute />}>
                    <Route path="organizations" element={<Outlet />}>
                      <Route index element={<AdminOrganizations />} />
                      <Route path=":id" element={<AdminOrganizationDetail />} />
                    </Route>
                    <Route path="faqs" element={<AdminFAQ />} />
                  </Route>

                  {/* Regular Admin Routes */}
                  <Route path=":organization_slug/plans" element={<AdminPlans />} />
                  <Route path="plans" element={<Outlet />}>
                    <Route index element={<AdminPlans />} />
                    <Route path=":token" element={<AdminPlanDetail />} />
                  </Route>

                  {/* Admin-only routes (guide admins cannot access) */}
                  <Route element={<AdminOnlyRoute />}>
                    <Route path=":organization_slug/users" element={<Users />} />
                    <Route path="users" element={<Users />} />
                  </Route>
                </Route>
              </Route>
              <Route path="/plan" element={<Plan />}>
                <Route index element={<OutletContextForwarder />} />
                <Route path="no-plans" element={<NoPlans />} />
                <Route path=":token" element={<OutletContextForwarder />}>
                  <Route path="invitation" element={<PlanInvitationAccept />} />
                  <Route path="download" element={<PlanDownload />} />
                  <Route path="checklist" element={<OutletContextForwarder />}>
                    <Route index element={<SetupChecklist />} />
                    <Route path="choose-guide" element={<ChooseGuide />} />
                    <Route path="guide-invited" element={<GuideInviteSent />} />
                    <Route path="invite-student" element={<InviteStudent />} />
                    <Route path="student-invited" element={<StudentInviteSent />} />
                    <Route path="sit-with-student" element={<SitWithStudent />} />
                    <Route path="invite-mentors" element={<InviteMentors />} />
                    <Route path="mentors-invited" element={<MentorsInviteSent />} />
                    <Route path="send-plan" element={<SendPlan />} />
                    <Route path="plan-complete" element={<PlanComplete />} />
                    <Route path="complete-mentor-activities" element={<MentorActivities />} />
                    <Route path="*" element={<NotFound />} />
                  </Route>
                  <Route path="activities" element={<PlanContent />} />\
                </Route>
              </Route>
              <Route path="/onboarding/tour" element={<OnboardingTourFlow />} />
              <Route path="/getting-started" element={<Navigate to="/plans" replace />} />
              <Route path="/how-it-works" element={<Navigate to="/plans" replace />} />
              <Route path="/begin-journey" element={<Navigate to="/plans" replace />} />
              <Route path="/plan/new" element={<PlanNewFlow />} />
              <Route path="/no-plans" element={<NoPlans />} />
              <Route path="/resources" element={<Resources />} />
            </Route>
            {import.meta.env.DEV && (
              <Route path="dev" element={<Outlet />}>
                <Route path="" element={<Dev />} />
                <Route path="setup" element={<SetupChecklist />} />
                <Route path="setup/choose-guide" element={<ChooseGuide />} />
                <Route path="setup/invite-student" element={<InviteStudent />} />
                <Route path="setup/sit-with-student" element={<SitWithStudent />} />
                <Route path="setup/invite-mentors" element={<InviteMentors />} />
                <Route path="setup/send-plan" element={<SendPlan />} />
                {DevRoutes.map((route, index) => (
                  <Route key={index} path={route.to} element={route.element} />
                ))}
              </Route>
            )}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </ErrorBoundary>
      </BrowserRouter>
      <DebugModal isOpen={debugOpen} setIsOpen={setDebugOpen} />
      <CookieConsentBanner user={user} />
    </>
  )
}

function AuthRoute() {
  const [user] = useAtom(userAtom)
  const [redirect, setRedirect] = useAtom(redirectAtom)
  const matchPlan = useMatch("/plan/:token")
  const matchResources = useMatch("/resources")

  if (user) {
    if (redirect) {
      const redirectPath = redirect
      setRedirect(null)
      return <Navigate to={redirectPath} replace />
    }
    return (
      <div>
        <Outlet />
      </div>
    )
  }
  if (matchPlan || matchResources) {
    setRedirect(window.location.pathname)
  }
  return <Navigate to="/register" />
}

function UnauthRoute() {
  const [user] = useAtom(userAtom)
  return user ? <Navigate to="/" /> : <Outlet />
}

function AdminRoute() {
  const [user] = useAtom(userAtom)
  if (!user || (!user.is_admin && !user.is_guide_admin && !user.is_guide)) {
    return <Navigate to="/" replace />
  }
  return <Outlet />
}

function AdminOnlyRoute() {
  const [user] = useAtom(userAtom)
  if (!user || !user.is_admin) {
    return <Navigate to="/admin" replace />
  }
  return <Outlet />
}

function AdminDefaultRoute() {
  const [user] = useAtom(userAtom)
  if (user && (user.is_guide || user.is_guide_admin) && !user.is_admin) {
    return <Navigate to="/admin/plans" replace />
  }
  return <AdminDashboard />
}

export default App
